import React from 'react';
import { getGrade, getScoreNum } from './shared';

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Sansation:wght@400;700&display=swap');`;

const getRingColor = (n) => {
  if (n >= 85) return '#10F090';
  if (n >= 70) return '#22D3EE';
  if (n >= 50) return '#F59E0B';
  return '#B5325B';
};

const getRingGlow = (n) => {
  if (n >= 85) return 'rgba(16,240,144,0.45)';
  if (n >= 70) return 'rgba(34,211,238,0.45)';
  if (n >= 50) return 'rgba(245,158,11,0.45)';
  return 'rgba(255,87,51,0.45)';
};

const getBadgeColor = (n) => {
  if (n >= 85) return '#34D399';
  if (n >= 70) return '#67E8F9';
  if (n >= 50) return '#FBD05B';
  return '#B5325B';
};

const getBarSoftBg = (n, barColor) => `${barColor}18`;

const getSoftPct = (val) => {
  if (val <= 0) return 0;
  return Math.round(Math.max(Math.min(90 - val * 0.68, 50), 28));
};

const AnimatedHeroRing = ({ score, isMobile = false }) => {
  const [v, setV] = React.useState(0);
  const size = isMobile ? 140 : 120;
  const rr = isMobile ? 62 : 54;
  const cc = 2 * Math.PI * rr;

  React.useEffect(() => {
    const safeScore = Number(score) || 0;
    const phases = [
      { target: Math.round(safeScore * 0.94), duration: 2500 },
      { target: Math.round(safeScore * 0.78), duration: 400  },
      { target: Math.round(safeScore * 0.92), duration: 300  },
      { target: safeScore,                    duration: 1200 },
    ];
    let cancelled = false, current = 0;
    const runPhase = (idx) => {
      if (idx >= phases.length || cancelled) return;
      const { target, duration } = phases[idx];
      const start = current, diff = target - start, t0 = performance.now();
      const tick = (now) => {
        if (cancelled) return;
        const p = Math.min((now - t0) / duration, 1);
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        const val = Math.round(start + diff * eased);
        setV(val); current = val;
        if (p < 1) requestAnimationFrame(tick);
        else { current = target; setTimeout(() => runPhase(idx + 1), 100); }
      };
      requestAnimationFrame(tick);
    };
    runPhase(0);
    return () => { cancelled = true; };
  }, [score]);

  const ringColor = getRingColor(v);
  const safeV     = Math.min(Math.max(Number(v) || 0, 0), 99.9);
  const filledArc = cc * (safeV / 100);
  const filterId  = React.useId().replace(/:/g, '');

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
        <defs>
          <filter id={filterId} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="blurAlpha" />
            <feFlood floodColor={ringColor} floodOpacity="0.75" result="color" />
            <feComposite in="color" in2="blurAlpha" operator="in" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={rr} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={isMobile ? 7 : 8} />
        <circle cx={size / 2} cy={size / 2} r={rr} fill="none"
          stroke={ringColor} strokeWidth={isMobile ? 7 : 8}
          strokeDasharray={`${filledArc} ${cc - filledArc}`}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
          style={{ transition: 'stroke-dasharray 0.1s' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: "'Sansation', sans-serif",
          fontSize: isMobile ? 42 : 36, 
          fontWeight: 800,
          color: ringColor, 
          lineHeight: 1, 
          letterSpacing: '-0.04em',
          textShadow: `0 0 24px ${ringColor}70`,
        }}>
          {v}
        </span>
        <span style={{
          fontSize: isMobile ? 9 : 8.5, 
          color: 'rgba(255,255,255,0.75)',
          fontWeight: 600, 
          marginTop: isMobile ? 4 : 4,
          letterSpacing: '0.12em', 
          textTransform: 'uppercase',
        }}>
          DecX Score
        </span>
      </div>
    </div>
  );
};

const Chevron = ({ open }) => (
  <svg width={13} height={13} viewBox="0 0 16 16"
    fill="none" stroke="#ffffff"
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: 1, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const PRIORITY_DOT = { High: '#B5325B', Medium: '#F59E0B', Low: '#10F090' };
const PRIORITY_PILL = {
  High:   { bg: 'rgba(255,87,51,0.18)',  color: '#FF7A5C', border: 'rgba(255,87,51,0.35)'  },
  Medium: { bg: 'rgba(245,158,11,0.18)', color: '#FBD05B', border: 'rgba(245,158,11,0.35)' },
  Low:    { bg: 'rgba(16,240,144,0.15)', color: '#34D399', border: 'rgba(16,240,144,0.30)' },
};

const EXPAND_CSS = `
  @keyframes so-expand {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
`;

const IssueRow = ({ issue, isLast, isMobile = false }) => {
  const [open, setOpen] = React.useState(false);
  const title    = issue.ISSUE_TITLE   || issue.priority_action_name    || issue.title       || '';
  const desc     = issue.ISSUE_DESC    || issue.priority_action_details || issue.description || null;
  const slides   = issue.ISSUE_SLIDES  || issue.slide_ref               || issue.slides      || null;
  const priority = issue.priority || null;
  const dot      = PRIORITY_DOT[priority]  || 'rgba(255,255,255,0.35)';
  const pill     = PRIORITY_PILL[priority] || null;

  return (
    <div style={{ borderBottom: isLast ? 'none' : '0.5px solid rgba(255,255,255,0.08)' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: isMobile ? 8 : 10, 
          padding: isMobile ? '8px 0' : '10px 0', 
          cursor: 'pointer', 
          userSelect: 'none' 
        }}>
        <span style={{ 
          width: isMobile ? 6 : 7, 
          height: isMobile ? 6 : 7, 
          borderRadius: '50%', 
          background: dot, 
          flexShrink: 0, 
          marginTop: isMobile ? 4 : 5, 
          boxShadow: `0 0 6px ${dot}80` 
        }} />
        <span style={{ 
          flex: 1, 
          fontSize: isMobile ? 12 : 13, 
          color: 'rgba(255,255,255,0.92)', 
          lineHeight: 1.4, 
          fontWeight: 500,
          wordBreak: 'break-word',
        }}>
          {title}
        </span>
        {pill && (
          <span style={{
            fontSize: isMobile ? 8.5 : 9.5, 
            fontWeight: 700,
            letterSpacing: isMobile ? '0.06em' : '0.08em', 
            textTransform: 'uppercase',
            background: pill.bg, 
            color: pill.color,
            border: `0.5px solid ${pill.border}`,
            padding: isMobile ? '2px 6px' : '2px 7px', 
            borderRadius: 4,
            flexShrink: 0, 
            alignSelf: 'center',
            whiteSpace: 'nowrap',
          }}>
            {priority}
          </span>
        )}
        <Chevron open={open} />
      </div>
      {open && (
        <div style={{
          marginBottom: isMobile ? 8 : 10, 
          marginLeft: isMobile ? 14 : 17,
          background: 'rgba(0,0,0,0.30)',
          borderRadius: isMobile ? 8 : 10, 
          padding: isMobile ? '12px 14px' : '14px 16px',
          display: 'flex', 
          flexDirection: 'column', 
          gap: isMobile ? 10 : 12,
          animation: 'so-expand 0.18s ease both',
          border: '0.5px solid rgba(255,255,255,0.14)',
          backdropFilter: 'blur(8px)',
        }}>
          {desc && (
            <div>
              <div style={{
                fontFamily: "'Sansation', sans-serif",
                fontSize: isMobile ? 8 : 9, 
                fontWeight: 700,
                letterSpacing: isMobile ? '0.10em' : '0.12em', 
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)', 
                marginBottom: isMobile ? 5 : 6,
              }}>Why It Matters</div>
              <div style={{ 
                fontSize: isMobile ? 11.5 : 12.5, 
                color: 'rgba(255,255,255,0.90)', 
                lineHeight: 1.65,
                wordBreak: 'break-word',
              }}>{desc}</div>
            </div>
          )}
          {slides && (
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, flexWrap: 'wrap' }}>
              <div style={{
                fontFamily: "'Sansation', sans-serif",
                fontSize: isMobile ? 8 : 9, 
                fontWeight: 700,
                letterSpacing: isMobile ? '0.10em' : '0.12em', 
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)', 
                flexShrink: 0,
              }}>Slide(s):</div>
              {String(slides).split(',').map((s, i) => (
                <span key={i} style={{
                  fontSize: isMobile ? 9.5 : 10.5, 
                  fontWeight: 600,
                  background: 'rgba(255,255,255,0.14)', 
                  color: 'rgba(255,255,255,0.9)',
                  border: '0.5px solid rgba(255,255,255,0.20)',
                  padding: isMobile ? '2px 6px' : '2px 8px', 
                  borderRadius: 5, 
                  whiteSpace: 'nowrap',
                }}>
                  {s.trim().startsWith('All') ? s.trim() : `#${s.trim()}`}
                </span>
              ))}
            </div>
          )}
          {!desc && !slides && (
            <div style={{ 
              fontSize: isMobile ? 11 : 12, 
              color: 'rgba(255,255,255,0.40)', 
              fontStyle: 'italic' 
            }}>No additional details available.</div>
          )}
        </div>
      )}
    </div>
  );
};

const IssuesList = ({ topIssues, totalIssues, setActiveSection, isMobile = false }) => (
  <div style={{ position: 'relative', zIndex: 1 }}>
    <style>{EXPAND_CSS}</style>
    <div style={{
      fontFamily: "'Sansation', sans-serif",
      fontSize: isMobile ? 10 : 11, 
      fontWeight: 700,
      textTransform: 'uppercase', 
      letterSpacing: isMobile ? '0.08em' : '0.10em',
      color: '#ffffff', 
      marginBottom: isMobile ? 10 : 14,
    }}>
      Top Issues Identified
    </div>
    {topIssues.length === 0 && (
      <div style={{ 
        fontSize: isMobile ? 12 : 13, 
        color: 'rgba(255,255,255,0.35)', 
        fontStyle: 'italic' 
      }}>No issues found.</div>
    )}
    {topIssues.map((issue, i) => (
      <IssueRow key={i} issue={issue} isLast={i === topIssues.length - 1 && totalIssues <= 5} isMobile={isMobile} />
    ))}
    {totalIssues > 5 && (
      <div
        style={{ 
          fontSize: isMobile ? 11 : 12, 
          color: 'rgba(147,210,255,0.70)', 
          marginTop: isMobile ? 10 : 12, 
          cursor: 'pointer' 
        }}
        onClick={() => setActiveSection && setActiveSection('all-issues')}
      >
        +{totalIssues - 5} more issues →
      </div>
    )}
  </div>
);

const normalizePriorities = (gp) => {
  if (!gp || typeof gp !== 'object') return { HIGH: [], MEDIUM: [], LOW: [] };
  const find = (keys) => { for (const k of keys) { if (Array.isArray(gp[k])) return gp[k]; } return []; };
  return {
    HIGH:   find(['HIGH', 'High', 'high']),
    MEDIUM: find(['MEDIUM', 'Medium', 'medium']),
    LOW:    find(['LOW', 'Low', 'low']),
  };
};

// ── Score Breakdown Bar — matches reference BarChart exactly ──────────────────
const ScoreBar = ({ cat, animatedWidth, isMobile = false, isTablet = false }) => {
  const n        = Number(animatedWidth) || 0;
  const barColor = getRingColor(n);
  const barGlow  = getRingGlow(n);

  const filledW = n * 0.82;
  const softW   = n * 0.18;

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: isMobile ? 10 : (isTablet ? 12 : 14),
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      <span style={{ 
        fontSize: isMobile ? 12 : 13, 
        fontWeight: 500, 
        color: '#6e6e73', 
        width: isMobile ? '100%' : (isTablet ? 100 : 120), 
        flexShrink: 0,
        textAlign: isMobile ? 'left' : 'left',
        wordBreak: 'break-word',
      }}>
        {cat.name}
      </span>
      <div style={{
        flex: 1, 
        width: isMobile ? '100%' : 'auto',
        height: isMobile ? 28 : 32,
        background: '#f4f4f6',
        borderRadius: isMobile ? 6 : 8, 
        overflow: 'hidden',
        display: 'flex', 
        flexDirection: 'row',
      }}>
        <div style={{
          width: `${filledW}%`,
          height: '100%',
          background: barColor,
          boxShadow: `0 0 10px ${barGlow}`,
          transition: 'width 0.8s ease',
          flexShrink: 0,
        }} />
        <div style={{
          width: `${softW}%`,
          height: '100%',
          background: `${barColor}22`,
          transition: 'width 0.8s ease',
          flexShrink: 0,
        }} />
      </div>
      <span style={{
        fontFamily: "'Sansation', sans-serif",
        fontSize: isMobile ? 16 : (isTablet ? 17 : 18), 
        fontWeight: 800,
        color: barColor,
        width: isMobile ? 'auto' : 40, 
        textAlign: 'right', 
        flexShrink: 0,
        alignSelf: isMobile ? 'flex-end' : 'center',
        position: isMobile ? 'absolute' : 'static',
        right: isMobile ? 0 : 'auto',
        top: isMobile ? 0 : 'auto',
      }}>
        {Number(getScoreNum(cat.score)) || 0}
      </span>
    </div>
  );
};

const ScoreOverview = ({
  iltcNum, startupData, slidesData,
  groupedPriorities, categoryScores, setActiveSection,
}) => {
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' && window.innerWidth < 640);
  const [isTablet, setIsTablet] = React.useState(typeof window !== 'undefined' && window.innerWidth >= 640 && window.innerWidth < 1024);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const safeScore      = Number(iltcNum) || 0;
  const safeCatScores  = Array.isArray(categoryScores) ? categoryScores : [];
  const safeSlidesData = Array.isArray(slidesData) ? slidesData : [];
  const priorities     = normalizePriorities(groupedPriorities);
  const grade          = getGrade(safeScore);
  const ringColor      = getRingColor(safeScore);
  const badgeColor     = getBadgeColor(safeScore);

  const topIssues = [
    ...priorities.HIGH.map(i   => ({ ...i, priority: i.priority || 'High'   })),
    ...priorities.MEDIUM.map(i => ({ ...i, priority: i.priority || 'Medium' })),
    ...priorities.LOW.map(i    => ({ ...i, priority: i.priority || 'Low'    })),
  ].slice(0, 5);

  const totalIssues = priorities.HIGH.length + priorities.MEDIUM.length + priorities.LOW.length;

  const [barWidths, setBarWidths] = React.useState(safeCatScores.map(() => 0));
  React.useEffect(() => {
    if (!safeCatScores.length) return;
    const t = setTimeout(() => setBarWidths(safeCatScores.map(c => getScoreNum(c.score))), 300);
    return () => clearTimeout(t);
  }, [categoryScores]);

  return (
    <div style={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
      <style>{FONT_CSS}</style>

      <h2 style={{
        fontFamily: "'Sansation', sans-serif",
        fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
        fontWeight: 700, 
        margin: isMobile ? '0 0 14px 0' : '0 0 20px 0',
        color: '#0f172a', 
        letterSpacing: '-0.02em',
      }}>
        myDecX Score Overview
      </h2>

      {/* Hero card */}
      {safeScore > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)',
          borderRadius: isMobile ? 16 : (isTablet ? 20 : 24), 
          padding: isMobile ? '20px 16px' : (isTablet ? '24px 20px' : 32),
          position: 'relative', 
          overflow: 'hidden',
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '45% 55%'),
          gap: isMobile ? 16 : (isTablet ? 20 : 24), 
          marginBottom: isMobile ? 14 : 20,
          boxShadow: '0 8px 32px rgba(0,70,200,0.25)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{ 
            position: 'absolute', 
            top: '-60%', 
            right: '-20%', 
            width: '55%', 
            height: '210%', 
            background: `radial-gradient(circle, ${ringColor}22 0%, transparent 60%)`, 
            pointerEvents: 'none' 
          }} />
          <div style={{ 
            position: 'absolute', 
            bottom: '-40%', 
            left: '10%', 
            width: '40%', 
            height: '120%', 
            background: 'radial-gradient(circle, rgba(0,30,100,0.35) 0%, transparent 70%)', 
            pointerEvents: 'none' 
          }} />

          {/* Left / Top Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: isMobile ? 'center' : 'center', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 16 : (isTablet ? 20 : 24), 
            position: 'relative', 
            zIndex: 1,
            textAlign: isMobile ? 'center' : 'left',
          }}>
            <AnimatedHeroRing score={safeScore} isMobile={isMobile} />
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: 1, 
              minWidth: 0,
              alignItems: isMobile ? 'center' : 'flex-start',
              width: isMobile ? '100%' : 'auto',
            }}>
              <div style={{ 
                fontSize: isMobile ? 8 : 9, 
                fontWeight: 600, 
                letterSpacing: isMobile ? '0.12em' : '0.14em', 
                textTransform: 'uppercase', 
                color: 'rgba(255,255,255,0.65)', 
                marginBottom: 4 
              }}>
                Audit Report
              </div>
              <div style={{ 
                fontFamily: "'Sansation', sans-serif", 
                fontSize: isMobile ? 22 : (isTablet ? 23 : 26), 
                fontWeight: 700, 
                color: '#ffffff', 
                letterSpacing: '-0.01em', 
                lineHeight: 1.15, 
                marginBottom: isMobile ? 8 : 8,
                wordBreak: 'break-word',
                width: '100%',
              }}>
                {startupData?.company_name || startupData?.startup_name || 'Pitch Deck'}
              </div>
              <div style={{
                display: 'inline-flex', 
                alignSelf: isMobile ? 'center' : 'flex-start',
                padding: isMobile ? '4px 12px' : '4px 12px', 
                borderRadius: 100, 
                marginBottom: isMobile ? 12 : 14,
                fontFamily: "'Sansation', sans-serif",
                fontSize: isMobile ? 11 : 11, 
                fontWeight: 700,
                background: `${badgeColor}22`, 
                color: badgeColor,
                border: `1px solid ${badgeColor}50`,
              }}>
                {grade?.label || '—'}
              </div>
              
              {/* Metadata - Mobile: 2-column grid, Desktop: inline flex */}
              {isMobile ? (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px 20px',
                  width: '100%',
                  marginBottom: 0,
                }}>
                  {(safeSlidesData.length > 0 || startupData?.slide_usage) && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        fontSize: 8, 
                        fontWeight: 600, 
                        letterSpacing: '0.12em', 
                        textTransform: 'uppercase', 
                        color: 'rgba(255,255,255,0.55)', 
                        marginBottom: 4 
                      }}>Slides</div>
                      <div style={{ 
                        fontFamily: "'Sansation', sans-serif", 
                        fontSize: 13, 
                        fontWeight: 700, 
                        color: 'rgba(255,255,255,0.92)' 
                      }}>
                        {safeSlidesData.length || startupData?.slide_usage}
                      </div>
                    </div>
                  )}
                  {startupData?.deck_type && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ 
                        fontSize: 8, 
                        fontWeight: 600, 
                        letterSpacing: '0.12em', 
                        textTransform: 'uppercase', 
                        color: 'rgba(255,255,255,0.55)', 
                        marginBottom: 4 
                      }}>Type</div>
                      <div style={{ 
                        fontFamily: "'Sansation', sans-serif", 
                        fontSize: 13, 
                        fontWeight: 700, 
                        color: 'rgba(255,255,255,0.92)', 
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}>
                        {startupData.deck_type}
                      </div>
                    </div>
                  )}
                  {startupData?.industry && (
                    <div style={{ 
                      textAlign: 'center',
                      gridColumn: (safeSlidesData.length > 0 || startupData?.slide_usage) && startupData?.deck_type ? 'span 2' : 'auto',
                    }}>
                      <div style={{ 
                        fontSize: 8, 
                        fontWeight: 600, 
                        letterSpacing: '0.12em', 
                        textTransform: 'uppercase', 
                        color: 'rgba(255,255,255,0.55)', 
                        marginBottom: 4 
                      }}>Industry</div>
                      <div style={{ 
                        fontFamily: "'Sansation', sans-serif", 
                        fontSize: 13, 
                        fontWeight: 700, 
                        color: 'rgba(255,255,255,0.92)', 
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}>
                        {startupData.industry}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ 
                    display: 'flex', 
                    gap: isTablet ? 20 : 24, 
                    alignItems: 'flex-start', 
                    marginBottom: 10,
                    flexWrap: 'wrap',
                  }}>
                    {(safeSlidesData.length > 0 || startupData?.slide_usage) && (
                      <div>
                        <div style={{ 
                          fontSize: 9, 
                          fontWeight: 600, 
                          letterSpacing: '0.14em', 
                          textTransform: 'uppercase', 
                          color: 'rgba(255,255,255,0.55)', 
                          marginBottom: 4 
                        }}>Slides</div>
                        <div style={{ 
                          fontFamily: "'Sansation', sans-serif", 
                          fontSize: 13, 
                          fontWeight: 700, 
                          color: 'rgba(255,255,255,0.92)' 
                        }}>
                          {safeSlidesData.length || startupData?.slide_usage}
                        </div>
                      </div>
                    )}
                    {startupData?.deck_type && (
                      <div>
                        <div style={{ 
                          fontSize: 9, 
                          fontWeight: 600, 
                          letterSpacing: '0.14em', 
                          textTransform: 'uppercase', 
                          color: 'rgba(255,255,255,0.55)', 
                          marginBottom: 4 
                        }}>Type</div>
                        <div style={{ 
                          fontFamily: "'Sansation', sans-serif", 
                          fontSize: 13, 
                          fontWeight: 700, 
                          color: 'rgba(255,255,255,0.92)', 
                          lineHeight: 1.4,
                          wordBreak: 'break-word',
                        }}>
                          {startupData.deck_type}
                        </div>
                      </div>
                    )}
                  </div>
                  {startupData?.industry && (
                    <div>
                      <div style={{ 
                        fontSize: 9, 
                        fontWeight: 600, 
                        letterSpacing: '0.14em', 
                        textTransform: 'uppercase', 
                        color: 'rgba(255,255,255,0.55)', 
                        marginBottom: 4 
                      }}>Industry</div>
                      <div style={{ 
                        fontFamily: "'Sansation', sans-serif", 
                        fontSize: 13, 
                        fontWeight: 700, 
                        color: 'rgba(255,255,255,0.92)', 
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                      }}>
                        {startupData.industry}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right — issues */}
          <IssuesList 
            topIssues={topIssues} 
            totalIssues={totalIssues} 
            setActiveSection={setActiveSection} 
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Score Breakdown */}
      {safeCatScores.length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '1fr 1fr'), 
          gap: isMobile ? 14 : 20,
          width: '100%',
          maxWidth: '100%',
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
            padding: isMobile ? '16px 14px' : (isTablet ? '20px 18px' : 24),
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            border: '0.5px solid rgba(0,0,0,0.06)',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
            <div style={{
              fontFamily: "'Sansation', sans-serif",
              fontSize: isMobile ? 13 : 14, 
              fontWeight: 700, 
              marginBottom: isMobile ? 14 : 20,
              color: '#1d1d1f',
            }}>
              Score Breakdown
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: isMobile ? 16 : 14,
              position: 'relative',
            }}>
              {safeCatScores.slice(0, 5).map((cat, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <ScoreBar 
                    cat={cat} 
                    animatedWidth={barWidths[i] || 0} 
                    isMobile={isMobile}
                    isTablet={isTablet}
                  />
                </div>
              ))}
            </div>
          </div>
          {!isMobile && !isTablet && (
            <div style={{ 
              background: '#ffffff', 
              borderRadius: 20, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
              border: '0.5px solid rgba(0,0,0,0.06)' 
            }} />
          )}
        </div>
      )}
    </div>
  );
};

export default ScoreOverview;